import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';
import {selectIsAuthed} from 'src/selectors/accounts';
import {selectResetPasswordError} from 'src/selectors/authentication';
import {
  resetPassword,
  updatePassword,
} from 'src/action-creators/authentication';

import PasswordInstruction from 'src/components/authentication/common/password-instruction/password-instruction';
import PasswordConfirmation from 'src/components/authentication/common/password-confirmation/password-confirmation';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import ResetConfirmation from 'src/components/authentication/reset/reset-confirmation';

import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {classify} from 'src/utils/classify';
import css from './reset-password.css';
import maincss from 'src/components/authentication/main.css';


const ResetPasswordContainer = () => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();

  const {isExtension} = useEnvironmentContext();

  const [resetToken, setResetToken] = React.useState<string>('');
  const [currentPassword, setCurrentPassword] = React.useState<string>('');
  const [passwordStrength, setPasswordStrength] = React.useState<number>(0);
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [disableReset, setDisableReset] = React.useState<boolean>(true);
  const [isResetSuccess, setIsResetSuccess] = React.useState<boolean>(false);
  const [isResetInProgress, setIsResetInProgress] =
    React.useState<boolean>(false);

  const isAgentLoggedIn = useSelector(selectIsAuthed);
  const resetPasswordError = useSelector(selectResetPasswordError);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_id');
    if (token) {
      setResetToken(token);
    }
  }, []);

  React.useEffect(() => {
    if (passwordStrength < 3) {
      setDisableReset(true);
    } else if (newPassword !== confirmPassword) {
      setDisableReset(true);
    } else if (!resetToken && !isAgentLoggedIn) {
      setDisableReset(true);
    } else if (!resetToken && !currentPassword) {
      setDisableReset(true);
    } else {
      setDisableReset(false);
    }
  }, [passwordStrength, currentPassword, newPassword, confirmPassword]);

  const handlePasswordReset = async () => {
    setIsResetInProgress(true);
    let apiData;
    if (resetToken) {
      apiData = await dispatch(
        resetPassword(resetToken, newPassword, confirmPassword),
      );
    } else {
      apiData = await dispatch(
        updatePassword(currentPassword, newPassword, confirmPassword),
      );
    }

    setIsResetInProgress(false);
    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    if (apiData) {
      setIsResetSuccess(true);
    }
  };

  return (
    <div className={maincss.contentSideBar}>
      {!isResetSuccess && (
        <div
          className={classify(maincss.containerContent, {
            [maincss.containerContentExt]: isExtension,
          })}
        >
          <div className={css.resetSection}>
            <>
              <TitleMedium>
                {getTranslatedText(t, CONSTANTS.RESET_PASSWORD_PAGE_TITLE)}
              </TitleMedium>
              <div className={css.instructions}>
                <PasswordInstruction />
              </div>
              {!resetToken && (
                <Input
                  label={t('Current_password', 'Current password')}
                  placeholder={t(
                    'Enter_current_password',
                    'Enter current password',
                  )}
                  size="medium"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              )}
              <div
                className={
                  resetToken
                    ? css.passwordConfirmationToken
                    : css.passwordConfirmationSignedIn
                }
              >
                <PasswordConfirmation
                  passwordLabel={t('New_password', 'New password')}
                  passwordPlaceholder={t('Enter_password', 'Enter password')}
                  confirmPasswordLabel={t(
                    'Confirm_password',
                    'Confirm password',
                  )}
                  confirmPasswordPlaceholder={t(
                    'Repeat_password',
                    'Repeat password',
                  )}
                  setPasswordStrengthValue={setPasswordStrength}
                  setNewPasswordValue={setNewPassword}
                  setConfirmPasswordValue={setConfirmPassword}
                />
              </div>
              <Button
                size="medium"
                type="primary"
                classNames={{wrapper: css.setNewBtn}}
                disabled={disableReset}
                onClick={handlePasswordReset}
              >
                {isResetInProgress
                  ? t('Setting_new_password', 'Setting new password')
                  : t('Set_new_password', 'Set new password')}
              </Button>
              {!resetToken && !isAgentLoggedIn && (
                <InContextAlert
                  semantic="danger"
                  classNames={{wrapper: css.alert}}
                >
                  {getTranslatedText(
                    t,
                    CONSTANTS['RESET_PASSWORD_DISABLED_ALERT'],
                  )}
                </InContextAlert>
              )}
              {!isResetSuccess && resetPasswordError && (
                <InContextAlert
                  semantic="danger"
                  classNames={{wrapper: css.alert}}
                >
                  {getTranslatedError(
                    t,
                    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                    ERROR_CODES[resetPasswordError] || resetPasswordError,
                  )}
                </InContextAlert>
              )}
            </>
          </div>
        </div>
      )}

      {isResetSuccess && (
        <div className={maincss.containerContentCentreAligned}>
          <ResetConfirmation />
        </div>
      )}
    </div>
  );
};

export default ResetPasswordContainer;
